import React, { useState } from 'react'
import {
    Autocomplete,
    Box,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Modal,
    Stack,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import GenericModal from '../GenericModal'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers'
import { useTranslation } from 'react-i18next'

interface ModalProps {
    open: boolean
    handleClose(v: boolean): void
    returnDates(iniD: Date, endD: Date): void
    hours?: boolean
    disableFuture?: boolean
}

export function CustomizeDateModal({
    open,
    handleClose,
    returnDates,
    hours,
    disableFuture = true,
}: ModalProps) {
    const {t} = useTranslation()
    const [iniD, setIniD] = useState<Date | null>(null)
    const [endD, setEndD] = useState<Date | null>(null)

    const validate = (): boolean => {
        if (!iniD || !endD) return false
        if (isNaN(iniD.getTime()) || isNaN(endD.getTime())) return false
        if (iniD.valueOf() > endD.valueOf()) return false
        return true
    }

    const showError =
        iniD &&
        endD &&
        !isNaN(iniD.getTime()) &&
        !isNaN(endD.getTime()) &&
        iniD.valueOf() > endD.valueOf()

    const DateComponent = hours ? DateTimePicker : DatePicker

    return (
        <GenericModal
            open={open}
            handleClose={handleClose}
            title="Selecione um período"
            textButton="Confirmar"
            handleSubmit={() => {
                if (iniD && endD) {
                    returnDates(iniD, endD)
                    handleClose(false)
                }
            }}
            disabledButton={!validate()}
        >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack>
                    <DateComponent
                        label={t("Data inicial")}
                        sx={{ width: '100%', mt: '10px' }}
                        format={hours ? 'dd/MM/yyyy hh:mm:ss' : 'dd/MM/yyyy'}
                        value={iniD}
                        onChange={(date) => setIniD(date)}
                        disableFuture={disableFuture}
                        ampm={false}
                    />
                    <DateComponent
                        label={t("Data Final")}
                        sx={{ width: '100%', mt: '10px' }}
                        format={hours ? 'dd/MM/yyyy hh:mm:ss' : 'dd/MM/yyyy'}
                        value={endD}
                        onChange={(date) => setEndD(date)}
                        disableFuture={disableFuture}
                        ampm={false}
                    />
                    {showError && (
                        <Typography
                            color="red"
                            fontSize={'14px'}
                            textAlign={'center'}
                            mt={'5px'}
                        >
                            {t('A data inicial deve ser anterior a final')}
                        </Typography>
                    )}
                </Stack>
            </LocalizationProvider>
        </GenericModal>
    )
}

export const DateFilterOptions: Record<string, number> = {
    Today: 1,
    ThisWeek: 2,
    LastWeek: 3,
    ThisMonth: 4,
    LastMonth: 5,
    ThisYear: 6,
    LastYear: 7,
    Customize: 8,
}

export const DateFilterOptionsLabels: Record<string, string> = {
    Today: 'Hoje',
    ThisWeek: 'Essa semana',
    LastWeek: 'Semana passada',
    ThisMonth: 'Esse mês',
    LastMonth: 'Mês passado',
    ThisYear: 'Esse ano',
    LastYear: 'Ano passado',
    Customize: 'Data customizada',
}

export const getPresetDate = (value: number) => {
    const today = new Date()
    const dayOfWeek = today.getDay()

    const firstDate: Date = new Date(today)
    const lastDate: Date = new Date(today)

    if (value === DateFilterOptions.ThisWeek) {
        firstDate.setDate(today.getDate() - dayOfWeek)
    } else if (value === DateFilterOptions.LastWeek) {
        firstDate.setDate(today.getDate() - dayOfWeek - 7)
        lastDate.setDate(firstDate.getDate() + 6)
    } else if (value === DateFilterOptions.ThisMonth) {
        firstDate.setDate(1)
    } else if (value === DateFilterOptions.LastMonth) {
        firstDate.setMonth(today.getMonth() - 1)
        firstDate.setDate(1)
        lastDate.setDate(0)
    } else if (value === DateFilterOptions.ThisYear) {
        firstDate.setMonth(0)
        firstDate.setDate(1)
    } else if (value === DateFilterOptions.LastYear) {
        firstDate.setFullYear(today.getFullYear() - 1)
        firstDate.setMonth(0)
        firstDate.setDate(1)

        lastDate.setFullYear(today.getFullYear() - 1)
        lastDate.setMonth(11)
        lastDate.setDate(31)
    }

    firstDate.setHours(0, 0, 0, 0)
    lastDate.setHours(23, 59, 59, 0)

    return { firstDate, lastDate, preset: value }
}

interface Props {
    returnDates(iniD: Date, endD: Date): void
}

export const DateFilterPicker = ({ returnDates }: Props) => {
    const {t} = useTranslation()
    const [openCustomizeModal, setOpenCustomizeModal] = useState(false)

    return (
        <>
            <CustomizeDateModal
                open={openCustomizeModal}
                handleClose={setOpenCustomizeModal}
                returnDates={returnDates}
            />
            <Autocomplete
                options={Object.keys(DateFilterOptions)
                    .filter((v) => !isNaN(Number(v)))
                    .map((key) => Number(key))}
                getOptionLabel={(value) => t(DateFilterOptionsLabels[value])}
                sx={{ width: '100%' }}
                value={null}
                onChange={(e, newDate) => {
                    if (newDate === null) return
                    if (newDate !== DateFilterOptions.Customize) {
                        const { firstDate, lastDate } = getPresetDate(newDate)
                        returnDates(firstDate, lastDate)
                    } else setOpenCustomizeModal(true)
                }}
                size="small"
                blurOnSelect={true}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t("Data da nota")}
                        sx={{ width: '100%' }}
                    />
                )}
            />
        </>
    )
}
